// Las tres herramientas con las que el agente ayuda DENTRO de la pantalla —
// ADR-2026-09-10-el-agente-toca-la-pantalla.
//
//   • ver_pantalla        — la ficha de la pantalla abierta: campos, valores, opciones.
//   • configurar_pantalla — pone valores en esos campos (validados contra la ficha).
//   • abrir_modo          — lleva al usuario a un modo, con productos ya elegidos.
//
// Son herramientas del CHAT (no del asistente de "Crear desde cero", que es él mismo
// una pantalla): se suman por `herramientasExtra` y se resuelven como silenciosas, sin
// tarjeta. Las definiciones son el CONTRATO (parámetros, resultado, cuándo sí y cuándo
// no); el CÓMO ayudar está en la skill `ayudar-en-un-modo`, y qué hace cada modo en
// `modos-de-imagen` y `modos-de-video`.
//
// Todo lo de acá es puro; el cableado (registro, navegación) está en `ChatAtlasContext`.

import {
  COPY_MODOS,
  kindDeModoParaRubro,
  MODES,
  modoVisiblePorFlags,
  rutaDeModo,
  VIDEO_INFLUENCER_ENABLED,
  type ModeCard,
} from '../pages/v2/modesCatalog';
import { getModePlanLock } from '../lib/planCapabilities';
import { textoDeDescripcion, type DescripcionDePantalla } from './puenteDePantalla';
import type { HerramientaChat } from './tipos';

export const NOMBRE_VER_PANTALLA = 'ver_pantalla';
export const NOMBRE_CONFIGURAR_PANTALLA = 'configurar_pantalla';
export const NOMBRE_ABRIR_MODO = 'abrir_modo';

export const NOMBRES_HERRAMIENTAS_PANTALLA = [
  NOMBRE_VER_PANTALLA,
  NOMBRE_CONFIGURAR_PANTALLA,
  NOMBRE_ABRIR_MODO,
] as const;

export const HERRAMIENTA_VER_PANTALLA: HerramientaChat = {
  name: NOMBRE_VER_PANTALLA,
  description:
    'Te muestra la FICHA de la pantalla que el usuario tiene abierta: en qué modo y paso está, cada campo en el orden de la pantalla (su id, cómo se llama, para qué sirve, qué tiene puesto ahora y qué opciones acepta), qué falta para poder generar y cuánto costaría. Gratis, sin UI, se puede llamar junto con otras herramientas.\n' +
    'USALA antes de explicarle un campo o de tocar algo con configurar_pantalla: los ids y las opciones válidas salen de acá, y cambian con lo que el usuario ya eligió (las poses compatibles, las vistas de sus productos). Si la pantalla no tiene campos, te lo dice y podés llevarlo a un modo con abrir_modo. En una pantalla de trabajo (el Estudio de Video) la ficha trae el ESTADO del proyecto (clips, selección, panel abierto) y la lista de sus herramientas propias.\n' +
    'Devuelve `{ hay_campos, hay_herramientas, texto }`.',
  input_schema: { type: 'object', properties: {}, required: [] },
};

export const HERRAMIENTA_CONFIGURAR_PANTALLA: HerramientaChat = {
  name: NOMBRE_CONFIGURAR_PANTALLA,
  description:
    'Pone valores en los campos de la pantalla que el usuario tiene abierta (el producto, el modelo, las poses, el fondo, el formato, la instrucción adicional, la cantidad… los que liste ver_pantalla). Lo que ponés se ve en pantalla AL INSTANTE y el usuario lo puede cambiar con un click; NO genera nada ni gasta créditos: el botón Generar es del usuario y no existe una herramienta para apretarlo.\n' +
    'Cada valor se valida contra la ficha: una opción tiene que ser uno de los ids (o etiquetas) que listó ver_pantalla, un número tiene que estar en su rango, un texto en su largo. Lo que no pasa se rechaza con un motivo y NO rompe nada; lo demás se aplica. Mandá todos los cambios de una en la misma llamada.\n' +
    'USALA cuando el usuario te pide que le configures algo ("ponele a Lucía", "elegí la remera y dos poses", "escribí la instrucción para que sea en la playa") o cuando le explicaste y aceptó que lo dejes armado. NO la uses para "generar": si pide que generes desde el chat, eso es generate_image; si está en un modo y pide "generá", configurá y decile que toque Generar.\n' +
    'Devuelve `{ aplicados, rechazados, texto }`; contale en UNA línea qué quedó.',
  input_schema: {
    type: 'object',
    properties: {
      cambios: {
        type: 'array',
        description:
          'Los cambios, uno por campo. `campo` es el id que listó ver_pantalla; `valor` es un id de opción (string), una lista de ids (para los campos de varias opciones), un texto, un número o true/false, según el tipo del campo.',
        items: {
          type: 'object',
          properties: {
            campo: { type: 'string', description: 'El id del campo (ej. "modelo", "poses", "instruccion").' },
            valor: {
              description:
                'El valor: string para una opción o un texto; array de strings para varias opciones; number; boolean.',
            },
          },
          required: ['campo', 'valor'],
        },
      },
    },
    required: ['cambios'],
  },
};

/** Cómo se resuelve un pedido de abrir un modo. */
export interface ContextoParaAbrirModo {
  esHogarDeco: boolean;
  esModa: boolean;
  isDevTester: boolean;
  plan: string | null;
}

export type ModoResuelto =
  | { ok: true; modo: ModeCard; kind: string; nombre: string; ruta: string }
  | { ok: false; motivo: string };

const normalizar = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()
    .replace(/^\/interfaz-v2\/modo\//, '')
    .replace(/^\//, '');

/** Nombre corto del modo, el mismo que usa la interfaz. */
export function nombreDeModo(m: ModeCard): string {
  return COPY_MODOS[m.path]?.titulo ?? m.preset.title;
}

/**
 * "Crear desde cero" y su variante: el agente NO los ofrece —
 * ADR-2026-09-13-crear-desde-cero-sale-de-las-listas. Anuncio tampoco: el modo actual
 * salió de toda la app hasta que llegue el nuevo Anuncios
 * (ADR-2026-10-05-anuncio-sale-de-toda-la-app); sigue vivo sólo por URL.
 *
 * Es la única excepción a la regla de abajo (los `ocultoEnListas` sí se abren desde el
 * chat) y tiene un motivo distinto al de Anuncio: ese modo ES un chat de generación, o
 * sea esta misma conversación con otra pantalla. Ofrecerlo sería mandar al usuario de un
 * chat a otro chat para hacer lo que ya puede pedir acá. Sigue vivo por URL para quien
 * tenga el link.
 */
const NO_LOS_OFRECE_EL_AGENTE = new Set(['asistente', 'asistente-v2', 'anuncio']);

/** Qué modos puede abrir ESTE usuario (los que ve en la app). */
export function modosAbribles(ctx: ContextoParaAbrirModo): ModeCard[] {
  return MODES.filter((m) => {
    if (m.path === 'video-influencer' && !VIDEO_INFLUENCER_ENABLED) return false;
    if (NO_LOS_OFRECE_EL_AGENTE.has(m.path)) return false;
    // Un `ocultoEnListas` sigue vivo por URL: desde el chat también se abre (salvo los de arriba).
    return modoVisiblePorFlags({ ...m, ocultoEnListas: false }, ctx);
  });
}

/**
 * Resuelve lo que el modelo pidió ("modelo", "Fotos de producto", "design-modelo", la
 * ruta) a un modo del catálogo que este usuario pueda abrir. El catálogo del rubro se
 * resuelve solo (Moda → `catalogo-ia`, Hogar y Deco → `catalogo-muebles`), igual que
 * en la app.
 */
export function resolverModoAAbrir(pedido: unknown, ctx: ContextoParaAbrirModo): ModoResuelto {
  if (typeof pedido !== 'string' || !pedido.trim()) {
    return { ok: false, motivo: 'Falta `modo`: el path del modo a abrir (ver la lista de la herramienta).' };
  }
  const buscado = normalizar(pedido);
  const candidato =
    MODES.find((m) => normalizar(m.path) === buscado) ??
    MODES.find((m) => typeof m.preset.kind === 'string' && normalizar(m.preset.kind) === buscado) ??
    MODES.find((m) => normalizar(nombreDeModo(m)) === buscado) ??
    MODES.find((m) => normalizar(m.preset.title) === buscado) ??
    null;
  if (!candidato) {
    return {
      ok: false,
      motivo: `No hay un modo "${pedido}". Los que puede abrir: ${modosAbribles(ctx)
        .map((m) => `${m.path} (${nombreDeModo(m)})`)
        .join(', ')}.`,
    };
  }
  // El catálogo del rubro: pedir "catalogo" en una marca de muebles abre el de muebles.
  const kindRubro = kindDeModoParaRubro(candidato.preset.kind, ctx.esHogarDeco);
  const modo =
    kindRubro !== candidato.preset.kind
      ? (MODES.find((m) => m.preset.kind === kindRubro) ?? candidato)
      : candidato;

  if (modo.path === 'anuncio') {
    // El modo actual salió de la app (ADR-2026-10-05-anuncio-sale-de-toda-la-app).
    return { ok: false, motivo: `${nombreDeModo(modo)} no está disponible por ahora.` };
  }
  if (modo.path === 'video-influencer' && !VIDEO_INFLUENCER_ENABLED) {
    // Nombra el MODO y no "el modo Video": los otros modos de video (Animar imagen,
    // Creador de video) siguen andando, y el copy viejo le decía al usuario que el video
    // entero estaba apagado.
    return { ok: false, motivo: `${nombreDeModo(modo)} no está disponible por ahora.` };
  }
  if (modo.soloHogarDeco && !ctx.esHogarDeco) {
    return { ok: false, motivo: `${nombreDeModo(modo)} es sólo para marcas del rubro Hogar y Decoración.` };
  }
  if (modo.ocultoEnHogarDeco && ctx.esHogarDeco) {
    return { ok: false, motivo: `${nombreDeModo(modo)} no aplica a una marca de Hogar y Decoración (pone el producto sobre una persona).` };
  }
  if (modo.soloModa && !ctx.esModa) {
    return { ok: false, motivo: `${nombreDeModo(modo)} es sólo para marcas del rubro Moda e Indumentaria.` };
  }
  if (modo.devOnly && !ctx.isDevTester) {
    return { ok: false, motivo: `${nombreDeModo(modo)} todavía no está disponible para esta cuenta.` };
  }
  const lock = getModePlanLock(modo.preset.kind, ctx.plan);
  if (lock) {
    return {
      ok: false,
      motivo: `${nombreDeModo(modo)} no está en el plan actual: ${lock.message} Ofrecele mejorar el plan desde Planes y suscripción.`,
    };
  }
  const ruta = rutaDeModo(modo.path);
  if (!ruta) return { ok: false, motivo: `El modo ${modo.path} no tiene ruta.` };
  return { ok: true, modo, kind: String(modo.preset.kind), nombre: nombreDeModo(modo), ruta };
}

/** La herramienta, con la lista de modos que ESTE usuario puede abrir en la descripción. */
export function herramientaAbrirModo(ctx: ContextoParaAbrirModo): HerramientaChat {
  const lista = modosAbribles(ctx)
    .map((m) => `${m.path} = ${nombreDeModo(m)}: ${COPY_MODOS[m.path]?.bajada ?? m.preset.description ?? ''}`)
    .join(' · ');
  return {
    name: NOMBRE_ABRIR_MODO,
    description:
      'Lleva al usuario a un MODO de la app (la pantalla donde se genera con selectores: producto, modelo, poses, fondo…), opcionalmente con productos ya elegidos, y te devuelve la ficha de esa pantalla para que sigas con configurar_pantalla. No genera ni gasta créditos.\n' +
      'USALA sólo cuando el usuario pidió ir a un modo o aceptó que lo lleves ("llevame al modo Modelo", "ayudame a hacer fotos de producto" → proponé el modo y, si dice que sí, abrilo). NO la uses si ya está en ese modo (usá ver_pantalla), ni para generar algo que podés hacer desde el chat con generate_image, ni sin avisarle: cambia la pantalla que está mirando.\n' +
      `Modos que puede abrir (path = nombre): ${lista}.\n` +
      'Devuelve `{ abierto, texto }` con la ficha de la pantalla (o el motivo si no se pudo).',
    input_schema: {
      type: 'object',
      properties: {
        modo: {
          type: 'string',
          description: 'El path del modo (ej. "modelo", "fotos-producto", "animar-imagen").',
        },
        product_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Productos del catálogo para dejar ya elegidos al entrar (los ids del prompt). Sólo si el usuario los nombró.',
        },
      },
      required: ['modo'],
    },
  };
}

/** Las de pantalla que viajan en cada envío. `abrir_modo` depende del usuario. */
export function herramientasDePantalla(ctx: ContextoParaAbrirModo): HerramientaChat[] {
  return [HERRAMIENTA_VER_PANTALLA, HERRAMIENTA_CONFIGURAR_PANTALLA, herramientaAbrirModo(ctx)];
}

/** Lee `product_ids` del input de `abrir_modo`, limpio. */
export function productIdsDelInput(input: unknown): string[] {
  if (!input || typeof input !== 'object') return [];
  const ids = (input as { product_ids?: unknown }).product_ids;
  if (!Array.isArray(ids)) return [];
  return Array.from(new Set(ids.filter((i): i is string => typeof i === 'string' && i.trim() !== '').map((i) => i.trim())));
}

export function resultadoDeAbrirModo(a: {
  resuelto: ModoResuelto;
  descripcion?: DescripcionDePantalla | null;
  yaEstaba?: boolean;
  productosNoAplicados?: string[];
}): Record<string, unknown> {
  if (!a.resuelto.ok) return { abierto: false, motivo: a.resuelto.motivo, texto: a.resuelto.motivo };
  const lineas: string[] = [];
  lineas.push(
    a.yaEstaba
      ? `El usuario ya estaba en ${a.resuelto.nombre}.`
      : `Se abrió ${a.resuelto.nombre} (${a.resuelto.ruta}). El usuario está viendo esa pantalla ahora.`,
  );
  if (a.productosNoAplicados && a.productosNoAplicados.length > 0) {
    lineas.push(`No se pudieron dejar elegidos estos productos: ${a.productosNoAplicados.join(', ')}.`);
  }
  if (a.descripcion) lineas.push(textoDeDescripcion(a.descripcion));
  else lineas.push('La pantalla no tiene campos para configurar desde el chat; ayudalo explicando.');
  return { abierto: true, ruta: a.resuelto.ruta, texto: lineas.join('\n') };
}
